import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Where a page puts its own words in the shell's head: the line between the places and Search. The
 * page fills it with a portal, so the head stays the one bar on every page and the places do not
 * move between pages.
 */
const HeadSlotContext = createContext<{
  el: HTMLElement | null;
  set: (el: HTMLElement | null) => void;
  writeLeft: boolean;
  leaveWrite: (left: boolean) => void;
}>({
  el: null,
  set: () => {},
  writeLeft: false,
  leaveWrite: () => {},
});

export function HeadSlotProvider({ children }: { children: ReactNode }) {
  const [el, set] = useState<HTMLElement | null>(null);
  const [writeLeft, leaveWrite] = useState(false);
  return <HeadSlotContext.Provider value={{ el, set, writeLeft, leaveWrite }}>{children}</HeadSlotContext.Provider>;
}

/** The slot itself, drawn once in the head. */
export function HeadSlot() {
  const { set } = useContext(HeadSlotContext);
  return <div ref={set} className="head-slot" />;
}

export function useHeadSlot(): HTMLElement | null {
  return useContext(HeadSlotContext).el;
}

/** Whether the page has its own Note in the slot, so the head's Write steps out. */
export function useWriteLeft(): boolean {
  return useContext(HeadSlotContext).writeLeft;
}

/** Called by a page whose bar carries Note: the head's Write leaves while it is mounted. */
export function useLeaveWrite(): void {
  const { leaveWrite } = useContext(HeadSlotContext);
  useEffect(() => {
    leaveWrite(true);
    return () => leaveWrite(false);
  }, [leaveWrite]);
}
